import React, { useEffect, useRef, useState } from 'react';
import { MapContainer, GeoJSON, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const REG_NORM = {
  "VALLE D'AOSTA/VALLÉE D'AOSTE": "VALLE D'AOSTA",
  "TRENTINO-ALTO ADIGE/SÜDTIROL": "TRENTINO-ALTO ADIGE",
  "FRIULI-VENEZIA GIULIA": "FRIULI VENEZIA GIULIA",
  "EMILIA-ROMAGNA": "EMILIA-ROMAGNA",
};
const normReg = (n) => (n ? (REG_NORM[n.toUpperCase()] || n.toUpperCase()) : '');
const normProv = (n) => (n || '').toUpperCase();

function FitBounds({ geo, filterFn, dep }) {
  const map = useMap();
  useEffect(() => {
    if (!geo) return;
    try {
      const layer = L.geoJSON(geo, filterFn ? { filter: filterFn } : undefined);
      const b = layer.getBounds();
      if (b.isValid()) map.fitBounds(b, { padding: [12, 12] });
    } catch (e) { /* ignore */ }
    setTimeout(() => map.invalidateSize(), 80);
  }, [geo, dep, map]); // eslint-disable-line
  return null;
}

// interpola lo -> base in funzione di t (0..1); `lo` = tonalità di partenza (tema-adattiva)
function ramp(lo, base, t) {
  const r = Math.round(lo[0] + (base[0] - lo[0]) * t);
  const g = Math.round(lo[1] + (base[1] - lo[1]) * t);
  const b = Math.round(lo[2] + (base[2] - lo[2]) * t);
  return `rgb(${r},${g},${b})`;
}

export default function AnalisiMap({ data, metric, drillRegion, selectedProv, onRegionClick, onProvinceClick,
  bg = '#fff', baseFill = '#f1f5f9', stroke = '#b8c2cc', rampLo = [238, 243, 239], baseOpacity = 0.35, zoom = true }) {
  const [regGeo, setRegGeo] = useState(null);
  const [provGeo, setProvGeo] = useState(null);

  useEffect(() => {
    fetch('/italy-regions.geojson').then(r => r.json()).then(setRegGeo).catch(() => {});
    fetch('/italy-provinces.geojson').then(r => r.json()).then(setProvGeo).catch(() => {});
  }, []);

  const mode = drillRegion ? 'province' : 'regioni';
  const dict = mode === 'province' ? (data?.province || {}) : (data?.regioni || {});

  const valueOf = (d) => (d ? metric.value(d) : 0);
  const inView = (key) => {
    const d = dict[key];
    if (!d) return false;
    if (mode === 'province' && d.reg !== drillRegion) return false;
    return true;
  };
  const maxVal = Math.max(1, ...Object.keys(dict).filter(inView).map(k => valueOf(dict[k])));

  const featureKey = (f) => (mode === 'province'
    ? normProv(f.properties.prov_name || f.properties.DEN_UTS || f.properties.NAME_2)
    : normReg(f.properties.reg_name));

  const styleFn = (f) => {
    const key = featureKey(f);
    const d = dict[key];
    const v = valueOf(d);
    const isSel = mode === 'province' && key === selectedProv;
    return {
      fillColor: d && inView(key) ? ramp(rampLo, metric.base, Math.min(1, v / maxVal)) : baseFill,
      weight: isSel ? 3 : 1,
      color: isSel ? '#0f172a' : stroke,
      fillOpacity: (d && inView(key)) ? 0.85 : baseOpacity,
    };
  };

  const onEach = (f, layer) => {
    const key = featureKey(f);
    const d = dict[key];
    const tip = d
      ? `<b>${key}</b><br>${metric.label}: <b>${metric.fmt(metric.value(d))}</b>`
        + `<br>NON TIM: ${d.nontim} · FTTH: ${d.ftth} · Lead caldi: ${d.lead_caldi}`
      : `<b>${key}</b><br>nessun dato`;
    layer.bindTooltip(tip, { sticky: true });
    layer.on('click', () => {
      if (mode === 'regioni') onRegionClick(key);
      else if (inView(key)) onProvinceClick(key);
    });
  };

  const geo = mode === 'province' ? provGeo : regGeo;
  const provFilter = mode === 'province'
    ? (f) => inView(featureKey(f))
    : undefined;

  if (!geo) return <div style={{ height: '100%', minHeight: 420, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>Caricamento mappa…</div>;

  return (
    <MapContainer center={[42, 12.5]} zoom={5} style={{ height: '100%', width: '100%', minHeight: 420, background: bg, borderRadius: 12 }}
      zoomControl={zoom} scrollWheelZoom attributionControl={false}>
      <GeoJSON key={`${mode}:${drillRegion || ''}:${metric.key}`} data={geo} style={styleFn} onEachFeature={onEach} filter={provFilter} />
      <FitBounds geo={geo} filterFn={provFilter} dep={`${mode}:${drillRegion || ''}`} />
    </MapContainer>
  );
}
